import { useCallback, useEffect, useState } from "react";
import { CalendarDays, Plus, Pencil, Trash2, CheckCircle2, Repeat, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { DatePicker } from "../DatePicker";

const AUD = [["ceo", "CEO"], ["team_leader", "Team Leader"], ["staff", "Staff"], ["counsellor", "Practice Suite"], ["qs_committee", "Q&S Committee"]];
const REPEATS = [["none", "Does not repeat"], ["weekly", "Weekly"], ["fortnightly", "Fortnightly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["annually", "Annually"]];
const REP_LABEL = Object.fromEntries(REPEATS);
const MON = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const fmtLong = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
const sel = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm";

function KeyDateForm({ initial, cats, onClose, onSaved }) {
  const [f, setF] = useState(initial || { title: "", start_date: "", category: "", repeats: "none", visible_to: [], description: "" });
  const [busy, setBusy] = useState(false);
  const up = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (a) => up("visible_to", f.visible_to.includes(a) ? f.visible_to.filter((x) => x !== a) : [...f.visible_to, a]);
  const save = async (e) => {
    e.preventDefault();
    if (!f.start_date) return toast.error("Pick a start date");
    if (!f.visible_to.length) return toast.error("Choose who can see this date");
    setBusy(true);
    try {
      const body = { title: f.title, start_date: f.start_date, category: f.category, repeats: f.repeats, visible_to: f.visible_to, description: f.description || "" };
      if (initial?.id) await api.put(`/key-dates/${initial.id}`, body); else await api.post("/key-dates", body);
      toast.success("Key date saved"); onSaved(); onClose();
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="key-date-form">
        <DialogHeader>
          <DialogTitle className="text-[#D9B95C] uppercase tracking-[0.08em]">{initial?.id ? "Edit Key Date" : "Add Key Date"}</DialogTitle>
          <DialogDescription>Add an organisational reminder date</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <label className="space-y-1.5"><span className="text-sm font-semibold">Title *</span>
            <Input required placeholder="e.g. Team Pay Day" value={f.title} onChange={(e) => up("title", e.target.value)} data-testid="kd-title" /></label>
          <div className="space-y-1.5"><span className="text-sm font-semibold">Start Date *</span>
            <DatePicker value={f.start_date} onChange={(v) => up("start_date", v)} testid="kd-start-date" /></div>
          <label className="space-y-1.5"><span className="text-sm font-semibold">Category *</span>
            <select required className={sel} value={f.category} onChange={(e) => up("category", e.target.value)} data-testid="kd-category">
              <option value="">Select category</option>{cats.map((c) => <option key={c}>{c}</option>)}
            </select></label>
          <label className="space-y-1.5"><span className="text-sm font-semibold">Repeats</span>
            <select className={sel} value={f.repeats} onChange={(e) => up("repeats", e.target.value)} data-testid="kd-repeats">
              {REPEATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <div className="sm:col-span-2 space-y-2"><span className="text-sm font-semibold">Visible To *</span>
            <div className="flex flex-wrap gap-2">
              {AUD.map(([v, l]) => (
                <label key={v} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${f.visible_to.includes(v) ? "border-[#C2A462] bg-[#C2A462]/10" : "border-input"}`} data-testid={`kd-visible-${v}`}>
                  <input type="checkbox" className="accent-[#C2A462]" checked={f.visible_to.includes(v)} onChange={() => toggle(v)} />{l}
                </label>
              ))}
            </div></div>
          <label className="sm:col-span-2 space-y-1.5"><span className="text-sm font-semibold">Description</span>
            <Textarea placeholder="Optional notes about this date" value={f.description} onChange={(e) => up("description", e.target.value)} data-testid="kd-description" /></label>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="kd-save">{busy ? "Saving…" : "Save Key Date"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SeriesDialog({ id, onClose, onEdit, reload }) {
  const [s, setS] = useState(null);
  const [moving, setMoving] = useState(null);
  const load = useCallback(() => api.get(`/key-dates/${id}/occurrences`).then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const complete = async (date) => { try { await api.post(`/key-dates/${id}/complete`, { date }); toast.success("Marked complete"); load(); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const change = async (date, new_date) => {
    if (!new_date && !window.confirm("Skip this date only? The rest of the series stays.")) return;
    try { await api.post(`/key-dates/${id}/occurrence`, { date, new_date }); toast.success(new_date ? "Date moved" : "Date skipped"); setMoving(null); load(); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const today = new Date().toLocaleDateString("en-CA");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="key-date-series">
        <DialogHeader>
          <DialogTitle className="text-[#D9B95C]">{s?.title || "Key date"}</DialogTitle>
          <DialogDescription>{s ? `${s.category} · ${REP_LABEL[s.repeats]}` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {s && (
          <div className="space-y-3">
            {s.description && <p className="text-sm text-muted-foreground">{s.description}</p>}
            <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{s.repeats === "none" ? "Date" : `All upcoming bookings (${s.upcoming.length})`}</div>
            <div className="max-h-80 overflow-y-auto space-y-1">
              {s.upcoming.map((d) => (
                <div key={d} className="rounded-md border border-[#C2A462]/20 px-3 py-1.5 text-sm" data-testid={`occurrence-${d}`}>
                  <div className="flex items-center gap-1">
                    <span className={`flex-1 ${d < today ? "text-red-400" : ""}`}>{fmtLong(d)}{d < today ? " · overdue" : ""}</span>
                    {s.can_edit && <>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setMoving(moving === d ? null : d)} data-testid={`move-${d}`}>Move</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => change(d, null)} data-testid={`skip-${d}`}>Skip</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => complete(d)} data-testid={`complete-${d}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Done</Button>
                    </>}
                  </div>
                  {moving === d && <div className="mt-1.5 flex items-center gap-2"><span className="text-xs text-muted-foreground">New date:</span><DatePicker value="" onChange={(v) => v && change(d, v)} testid={`move-picker-${d}`} /></div>}
                </div>
              ))}
              {!s.upcoming.length && <div className="text-sm text-muted-foreground">No upcoming dates.</div>}
            </div>
            {s.can_edit && <Button variant="outline" size="sm" onClick={() => onEdit(s)} data-testid="series-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1" />Edit whole series</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ManageDialog({ onClose, onEdit, onOpen, reload }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => api.get("/key-dates").then((r) => setList(r.data)).catch(() => setErr(true)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (k) => { if (!window.confirm(`Delete "${k.title}"?`)) return; try { await api.delete(`/key-dates/${k.id}`); toast.success("Deleted"); load(); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const complete = async (k) => { try { await api.post(`/key-dates/${k.id}/complete`, {}); toast.success("Next date marked complete"); load(); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="key-dates-manage">
        <DialogHeader>
          <DialogTitle className="text-[#D9B95C] uppercase tracking-[0.08em]">Manage Key Dates</DialogTitle>
          <DialogDescription>Edit, complete or remove organisational dates</DialogDescription>
        </DialogHeader>
        {err && <div className="text-sm text-destructive">Unable to load Key Dates.</div>}
        {!list && !err && <div className="text-sm text-muted-foreground">Loading…</div>}
        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {(list || []).map((k) => (
            <div key={k.id} className="flex flex-wrap items-center gap-3 rounded-md border px-4 py-3" data-testid={`manage-row-${k.id}`}>
              <button className="flex-1 min-w-0 text-left" onClick={() => onOpen(k.id)}>
                <div className="font-semibold">{k.title}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">{k.category} · {k.repeats !== "none" && <Repeat className="h-3 w-3" />}{REP_LABEL[k.repeats]} · next {k.next_date ? fmtLong(k.next_date) : "none"}{k.repeats !== "none" && <span className="text-[#C2A462]"> · view all bookings</span>}</div>
                {k.description ? <div className="mt-1.5 flex items-start gap-1.5 text-[13px] text-slate-300" data-testid={`manage-desc-${k.id}`}><FileText className="h-3.5 w-3.5 mt-0.5 text-[#C2A462] shrink-0" /><span className="line-clamp-2">{k.description}</span></div>
                  : <div className="mt-1 text-[12px] text-muted-foreground/60 italic">No description</div>}
              </button>
              {k.can_edit && <>
                <Button size="sm" variant="ghost" onClick={() => complete(k)} disabled={!k.next_date} data-testid={`manage-complete-${k.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onEdit(k)} data-testid={`manage-edit-${k.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(k)} data-testid={`manage-delete-${k.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </>}
            </div>
          ))}
          {list && !list.length && <div className="text-sm text-muted-foreground">No key dates yet.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function DateRow({ o, onClick }) {
  const d = new Date(`${o.date}T00:00:00`);
  const a = o.days_away;
  const [dot, txt, when] = a < 0 ? ["bg-red-500", "text-red-400", a === -1 ? "Overdue 1 day" : `Overdue ${-a} days`]
    : a === 0 ? ["bg-emerald-500", "text-emerald-400", "Today"]
    : a <= 2 ? ["bg-orange-500", "text-orange-400", a === 1 ? "Tomorrow" : "in 2 days"]
    : ["bg-[#C2A462]/50", "text-muted-foreground", `in ${a} days`];
  return (
    <button onClick={onClick} data-testid={`key-date-item-${o.key_date_id}-${o.date}`} data-status={a < 0 ? "overdue" : a === 0 ? "today" : a <= 2 ? "soon" : "upcoming"}
      className="flex w-full items-center gap-3 border-b border-white/5 px-1 py-1.5 text-left text-[13px] transition-colors last:border-0 hover:bg-white/[0.03]">
      <i className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
      <span className="w-[88px] shrink-0 tabular-nums text-slate-400">{DOW[d.getDay()]} {d.getDate()} {MON[d.getMonth()].charAt(0) + MON[d.getMonth()].slice(1).toLowerCase()}</span>
      <span className="min-w-0 flex-1 truncate text-slate-100">{o.title}</span>
      <span className={`shrink-0 text-xs ${txt}`}>{when}</span>
    </button>
  );
}

const GROUPS = [["Overdue", (a) => a < 0], ["This week", (a) => a >= 0 && a < 7], ["Next week", (a) => a >= 7 && a < 14], ["Later", (a) => a >= 14]];

export function KeyDatesWidget() {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ categories: [], can_edit: false });
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get("/key-dates/upcoming?days=60").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); api.get("/key-dates/meta").then((r) => setMeta(r.data)).catch(() => {}); }, [load]);
  const edit = (k) => setDlg({ t: "form", k });
  return (
    <div className="rounded-md border border-[#C2A462]/30 bg-gradient-to-b from-[#141e33] to-[#0f1727] p-4 h-full" data-testid="widget-key-dates">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#C2A462]" /><span className="text-sm font-semibold tracking-wide">Key Dates</span>
          <span className="text-[11px] text-muted-foreground" data-testid="key-dates-count">{items ? `· ${items.filter((o) => o.days_away >= 0 && o.days_away < 7).length} this week` : ""}</span></div>
        <div className="flex items-center gap-1">
          {meta.can_edit && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#D9B95C]" onClick={() => setDlg({ t: "form" })} data-testid="add-key-date-btn"><Plus className="h-3.5 w-3.5 mr-0.5" />Add</Button>}
          {meta.can_edit && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setDlg({ t: "manage" })} data-testid="manage-key-dates-btn">Manage</Button>}
        </div>
      </div>
      <div className="max-h-[300px] space-y-3 overflow-y-auto pr-1">
        {GROUPS.map(([label, test]) => {
          const rows = (items || []).filter((o) => test(o.days_away));
          if (!rows.length) return null;
          return <div key={label} data-testid={`key-dates-group-${label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#C2A462]/80">{label}</div>
            {rows.map((o) => <DateRow key={`${o.key_date_id}-${o.date}`} o={o} onClick={() => setDlg({ t: "series", id: o.key_date_id })} />)}
          </div>;
        })}
        {items && !items.length && <div className="text-xs text-muted-foreground py-2">Nothing coming up in the next 60 days.</div>}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-muted-foreground" data-testid="key-dates-legend">
        <span className="flex items-center gap-1"><i className="h-1.5 w-1.5 rounded-full bg-orange-500" />Due in 2 days</span>
        <span className="flex items-center gap-1"><i className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Today</span>
        <span className="flex items-center gap-1"><i className="h-1.5 w-1.5 rounded-full bg-red-500" />Overdue</span>
      </div>
      {dlg?.t === "form" && <KeyDateForm initial={dlg.k} cats={meta.categories} onClose={() => setDlg(null)} onSaved={load} />}
      {dlg?.t === "manage" && <ManageDialog onClose={() => setDlg(null)} onEdit={edit} onOpen={(id) => setDlg({ t: "series", id })} reload={load} />}
      {dlg?.t === "series" && <SeriesDialog id={dlg.id} onClose={() => setDlg(null)} onEdit={edit} reload={load} />}
    </div>
  );
}
